/**
 * Learning Object List
 * 
 * Renders a list of learning objects, suitable for the front page of the DLC.
 * The component has a number of features and behaviours to handle various types of lists.  
 * 
 * More link - An optional more link to link to more content
 * Pager - If the length of the object_list prop exceeds the per_page prop, a pager will render.
 *
 * Example usage: 
 * <LearningObjectList per_page={2} object_list={learning_objects} more_link="https://www.lib.uoguelph.ca/top-picks" more_text="More top picks" />
 * 
 * @param {*} object_list An array of learing object nodes, like what you would get from the allNodeDlcContent graphql endpoint 
 * @param {int} per_page How many items to render per page (default: 4).
 * @param {string} more_link Optional URL to use for a more link. If this and more_text aren't set, the more link does not show up.
 * @param {string or JSX} more_text Optional Either a string, or JSX to use for the content of the more link. If this and more_link aren't set, the more link does not show up.
 * @returns JSX for a list of learning objects.
 */

// A minimal example query that would be suitable to generate the list of learning objects for the object_list parameter: 
// items: allNodeDlcContent {
//     nodes {
//         id
//         field_dlc_thumbnail {
//             alt
//         }
//         title
//         path {
//             alias
//         }
//         relationships {
//             field_dlc_thumbnail {
//                 localFile {
//                     childImageSharp {
//                         gatsbyImageData(width: 300, height: 300)
//                     }
//                 }
//             }
//         }
//     }
// }

import React from 'react';
import { v4 as uuid } from 'uuid';

import { Link } from 'gatsby'
import PropTypes from "prop-types"
import { useState, useEffect} from 'react';
import { GatsbyImage } from "gatsby-plugin-image";

import * as styles from 'src/dlc_styles/learningObjectList.module.css'

const LearningObjectList = ({ object_list, per_page, more_link, more_text }) => {

    const [learning_objects, setLearningObjects] = useState(object_list);
    const [next_disabled, setNextDisabled] = useState(false);
    const [prev_disabled, setPrevDisabled] = useState(true);
    const [page, setPage] = useState(0);

    useEffect(() => {
        function update(page) {
            if (per_page !== 0) {
                let total = Math.ceil(object_list.length / per_page);
                let start = page * per_page;
                let end = (page + 1) * per_page;

                setLearningObjects(object_list.slice(start, end));
                setNextDisabled(!(page < total -1));
                setPrevDisabled(!(page >= 1));
            }
        }

        update(page)
    }, [page, per_page, object_list])

    if (object_list === null) {
        return <></>;
    }

    const rendered_list = learning_objects.map((item) => {
        return <li key={ item.id } className={ styles.list_item }>
            <Link to={ item.path.alias }>
                <GatsbyImage image={ item.relationships.field_dlc_thumbnail.localFile.childImageSharp.gatsbyImageData } alt={ item.field_dlc_thumbnail.alt }/>
            </Link>            
        </li>
    })

    const list_id = uuid();

    let pager_rendered = <></>;
    const is_paginated = object_list.length > per_page;
    if ( is_paginated ) {
        const page_next = () => {
            setPage(page + 1);
        }
    
        const page_prev = () => {
            setPage(page - 1);
        }
    
        let total = Math.ceil(object_list.length / per_page);

        pager_rendered = 
            <div className={styles.pagination}>
                <button aria-controls={list_id} disabled={prev_disabled} className="btn btn-primary" onClick={page_prev}>Previous</button>
                <span>Page {page + 1} of {total}</span>
                <button aria-controls={list_id} disabled={next_disabled} className="btn btn-primary" onClick={page_next}>Next</button>
            </div>;
    }

    let more_link_renderd = <></>;
    if ( is_paginated ) {
        if ( more_link !== '' && more_text !== '') {
            more_link_renderd = <div className={ styles.more_link_wrapper }>
                <a href={ more_link } className=" btn btn-outline-info">{ more_text }</a>
            </div>;
        }
    }

    let result = 
    <div className={ styles.learning_object_list_wrapper }>
        <ul aria-live="polite" id={list_id} className={ styles.learning_object_list }>
            { rendered_list }
        </ul>
        
        { pager_rendered }
        { more_link_renderd }
    </div>

    return result;
}

LearningObjectList.propTypes = {
    object_list: PropTypes.array,
    per_page: PropTypes.number,
    more_link: PropTypes.string,
    more_text: PropTypes.any,
}
  
LearningObjectList.defaultProps = {
    object_list: [],
    per_page: 4,
    more_link: '',
    more_text: '',
}

export default LearningObjectList
